import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import type { NodeProps } from 'reactflow';
import { BaseNodeUI } from './BaseNodeUI';
import { Network } from 'lucide-react';

export default memo(({ selected, data }: NodeProps) => {
    // 5 input handles: input-0 (center/star), input-1 .. input-4 (dimension tables)
    const inputs = [0, 1, 2, 3, 4];

    // Total node height to contain 5 handles comfortably
    const nodeHeight = 160;

    // Distribution: distribute handles evenly along the border
    const handlePositions = inputs.map((i) => {
        return Math.round(((i + 1) / (inputs.length + 1)) * nodeHeight);
    });

    return (
        <div
            className="relative"
            style={{ width: 200, height: nodeHeight }}
        >
            {/* Taller BaseNodeUI using the new className prop */}
            <BaseNodeUI
                title={data.label || "Star Join"}
                icon={Network}
                colorClass=""
                bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700"
                className="!h-[160px]" // Force the inner box to be 160px tall
                selected={selected}
                hideHandles={true}
                isRunning={data?.executionStatus === 'Running'}
                executionStatus={data?.executionStatus}
            />

            {/* ── INPUT HANDLES (Left Side) ── */}
            {inputs.map((i) => {
                const topPx = handlePositions[i];
                const isStar = i === 0;

                return (
                    <div key={i}>
                        <Handle
                            type="target"
                            position={Position.Left}
                            id={`input-${i}`}
                            isConnectable={true}
                            className="!w-3 !h-3 !min-w-0 !min-h-0 !bg-white !border-2 z-50 cursor-crosshair flex items-center justify-center p-0"
                            style={{
                                top: topPx,
                                left: -6,
                                transform: 'translateY(-50%)',
                                borderColor: isStar ? '#f59e0b' : '#94a3b8'
                            }}
                        >
                            {/* Inner dot for Star handle */}
                            {isStar && <div className="w-1 h-1 bg-amber-500 rounded-full" />}
                        </Handle>

                        {/* Label - Positioned to the left of the handle */}
                        <div
                            className="absolute text-[9px] font-bold pointer-events-none select-none whitespace-nowrap"
                            style={{
                                top: topPx,
                                left: -24,
                                transform: 'translateY(-50%)',
                                color: isStar ? '#d97706' : '#94a3b8',
                                textAlign: 'right',
                                width: 14
                            }}
                        >
                            {isStar ? '★' : i}
                        </div>
                    </div>
                );
            })}

            {/* ── OUTPUT HANDLE (Right Side, Center) ── */}
            <Handle
                type="source"
                position={Position.Right}
                id="success"
                isConnectable={true}
                className="!w-3 !h-3 !min-w-0 !min-h-0 !bg-white !border-2 !border-emerald-500 z-50 cursor-crosshair flex items-center justify-center p-0"
                style={{ right: -6, top: '50%', transform: 'translateY(-50%)' }}
            />
        </div>
    );
});
